import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Space,
  Select,
  InputNumber,
  Switch,
  Typography,
  Divider,
  Row,
  Col,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'store';
import {
  createTrigger,
  updateTrigger,
  fetchTrigger,
  fetchTemplates,
  fetchSegments,
  fetchCampaigns,
  selectTriggers,
  selectTemplates,
  selectSegments,
  selectCampaigns,
  selectPushLoading,
  selectCurrentTrigger,
  setCurrentTrigger,
} from 'store/slices/push.slice';
import {
  fetchApplications,
  selectApplications,
} from 'store/slices/applications.slice';
import { EventTriggerCreateRequest } from 'types/push_management.types';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import '../../styles/Common.css';
import {getStoredAppId} from 'utils/pushCenterStorage'

const { Title, Text: AntText } = Typography;

const EventTriggerForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const isEditMode = Boolean(id);
  const applications = useAppSelector(selectApplications);
  const triggers = useAppSelector(selectTriggers);
  const templates = useAppSelector(selectTemplates);
  const segments = useAppSelector(selectSegments);
  const campaigns = useAppSelector(selectCampaigns);
  const loading = useAppSelector(selectPushLoading);
  const currentTrigger = useAppSelector(selectCurrentTrigger);

  const [selectedApp, setSelectedApp] = useState<number | null>(null);


  useEffect(() => {
    dispatch(fetchApplications({ limit: 100, offset: 0 }));

    if (isEditMode && id) {
      dispatch(fetchTrigger(parseInt(id)));
    } else {
      dispatch(setCurrentTrigger(null));
      const storedAppId = getStoredAppId();
      if (storedAppId) {
        setSelectedApp(storedAppId);
        form.setFieldValue('app_id', storedAppId);
      }
    }
  }, [dispatch, isEditMode, id]);

  useEffect(() => {
    if (!isEditMode || !id) return;

    const triggerId = parseInt(id);
    const trigger =
      currentTrigger?.id === triggerId
        ? currentTrigger
        : triggers.find(t => t.id === triggerId);

    if (!trigger) return;

    setSelectedApp(trigger.app_id);
    form.setFieldsValue({
      app_id: trigger.app_id,
      name: trigger.name,
      trigger_type: trigger.trigger_type,
      push_template_id: trigger.push_template_id,
      segment_id: trigger.segment_id ?? undefined,
      campaign_id: trigger.campaign_id ?? undefined,
      delay_minutes: trigger.delay_minutes,
      is_active: trigger.is_active,
      max_sends_per_user: trigger.max_sends_per_user,
      cooldown_hours: trigger.cooldown_hours,
    });
  }, [isEditMode, id, currentTrigger, triggers, form]);

  useEffect(() => {
    if (selectedApp) {
      dispatch(fetchTemplates(selectedApp));
      dispatch(fetchSegments({ appId: selectedApp }));
      dispatch(fetchCampaigns({ appId: selectedApp, limit: 1000 }));
    }
  }, [dispatch, selectedApp]);

  const handleSubmit = async (values: any) => {
    try {
      if (isEditMode && id) {
        await dispatch(updateTrigger({
          id: parseInt(id),
          data: {
            name: values.name,
            push_template_id: values.push_template_id,
            segment_id: values.segment_id ?? null,
            campaign_id: values.campaign_id ?? null,
            delay_minutes: values.delay_minutes,
            is_active: values.is_active,
            max_sends_per_user: values.max_sends_per_user,
            cooldown_hours: values.cooldown_hours,
          },
        })).unwrap();
        notify.success('Триггер успешно обновлен');
      } else {
        await dispatch(createTrigger({
          app_id: values.app_id,
          name: values.name,
          trigger_type: values.trigger_type,
          push_template_id: values.push_template_id,
          segment_id: values.segment_id ?? null,
          campaign_id: values.campaign_id ?? null,
          delay_minutes: values.delay_minutes,
          is_active: values.is_active,
          max_sends_per_user: values.max_sends_per_user,
          cooldown_hours: values.cooldown_hours,
        } as EventTriggerCreateRequest)).unwrap();
        notify.success('Триггер успешно создан');
      }

      navigate(ROUTES.PUSH_CENTER);
    } catch (error: any) {
      notify.error(error.message || 'Ошибка при сохранении триггера');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(ROUTES.PUSH_CENTER)}
          >
            Назад
          </Button>
          <Title level={2}>
            {isEditMode ? 'Редактирование триггера' : 'Создание триггера'}
          </Title>
        </Space>
      </div>
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            delay_minutes: 0,
            max_sends_per_user: 1,
            cooldown_hours: 24,
          }}
        >
          <div>
            <AntText strong style={{ fontSize: 16 }}>Основные настройки</AntText>
            <Divider />
          </div>
          <Form.Item
            label="Приложение"
            name="app_id"
            rules={[{ required: true, message: 'Выберите приложение' }]}
          >
            <Select
              placeholder="Выберите приложение"
              size="large"
              showSearch
              optionFilterProp="children"
              disabled={isEditMode}
              onChange={setSelectedApp}
            >
              {applications.map(app => (
                <Select.Option key={app.id} value={app.id}>
                  {app.name} ({app.bundle_id})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Название триггера"
            name="name"
            rules={[
              { required: true, message: 'Введите название' },
              { min: 3, message: 'Минимум 3 символа' },
              { max: 100, message: 'Максимум 100 символов' },
            ]}
          >
            <Input
              placeholder="Например: Welcome after Registration"
              size="large"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Тип события"
                name="trigger_type"
                rules={[{ required: true, message: 'Выберите тип события' }]}
              >
                <Select placeholder="Выберите событие" size="large" disabled={isEditMode}>
                  <Select.Option value="install">
                    Установка
                  </Select.Option>
                  <Select.Option value="registration">
                    Регистрация
                  </Select.Option>
                  <Select.Option value="deposit">
                    Депозит (любой)
                  </Select.Option>
                  <Select.Option value="first_deposit">
                    Первый депозит
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Задержка перед отправкой (минуты)"
                name="delay_minutes"
                rules={[{ required: true, message: 'Укажите задержку' }]}
                extra="0 = отправить сразу, 60 = через 1 час после события"
              >
                <InputNumber
                  placeholder="60"
                  size="large"
                  min={0}
                  max={43200}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ marginTop: 24 }}>
            <AntText strong style={{ fontSize: 16 }}>Контент и аудитория</AntText>
            <Divider />
          </div>
          <Form.Item
            label="Шаблон push-уведомления"
            name="push_template_id"
            rules={[{ required: true, message: 'Выберите шаблон' }]}
          >
            <Select
              placeholder="Выберите шаблон"
              size="large"
              disabled={!selectedApp}
              showSearch
              optionFilterProp="children"
              optionLabelProp="label"
            >
              {templates.map(template => (
                <Select.Option 
                  key={template.id} 
                  value={template.id}
                  label={template.name}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{template.name}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {template.title}
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Кампания (опционально)"
            name="campaign_id"
            extra="Привязка к кампании для аналитики"
          >
            <Select
              placeholder="Выберите кампанию"
              size="large"
              disabled={!selectedApp}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {campaigns.map(campaign => (
                <Select.Option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Сегмент (опционально)"
            name="segment_id"
            extra={
                    <span>
                      Сегмент проверяется и при срабатывании события, и перед отправкой
                      отложенного пуша. Если юзер вышел из сегмента (например, зарегался),
                      pending пуш не уйдёт. Не выбран — триггер срабатывает для всех, без проверок.
                    </span>
                  }
          >
            <Select
              placeholder="Все пользователи"
              size="large"
              disabled={!selectedApp}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {segments.map(segment => (
                <Select.Option key={segment.id} value={segment.id}>
                  {segment.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ marginTop: 24 }}>
            <AntText strong style={{ fontSize: 16 }}>Ограничения</AntText>
            <Divider />
          </div>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Максимум отправок на пользователя"
                name="max_sends_per_user"
                rules={[{ required: true, message: 'Укажите максимум' }]}
                extra="Сколько раз триггер может сработать для одного пользователя"
              >
                <InputNumber
                  placeholder="1"
                  size="large"
                  min={1}
                  max={100}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Cooldown между отправками (часы)"
                name="cooldown_hours"
                rules={[{ required: true, message: 'Укажите cooldown' }]}
                extra="Минимальный интервал между отправками одному пользователю"
              >
                <InputNumber
                  placeholder="24"
                  size="large"
                  min={1}
                  max={720}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Статус"
            name="is_active"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Активен"
              unCheckedChildren="Неактивен"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                size="large"
                loading={loading}
              >
                {isEditMode ? 'Сохранить изменения' : 'Создать триггер'}
              </Button>
              <Button
                size="large"
                onClick={() => navigate(ROUTES.PUSH_CENTER)}
              >
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default EventTriggerForm;